import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { getLocationsResponseData, useLocationsStore } from './useLocationsStore'
import { findBestMatchingGlobalID, useSessionStore } from './useSessionStore'
import { hydrateUserDeviceLocationFromSession, useUserDeviceLocationStore } from './useUserDeviceLocationStore'
import { asTrimmedId, logger, resolveChatEnabled, showErrorToast } from '@/utils'
import { createPersistOptions } from '@/utils/zustand-persist'
import { WidgetBreakpointTab, WidgetStep } from '@/types'
import type { EcomLocation } from '@/types/widget'
import { getWidgetConfig, isEmbedded, setWidgetConfig } from '@/config'
import { initApiBase, lookupDeviceContacts } from '@/api/widget'
import { widgetBridge } from '@/bridge'
import { track } from '@/utils/analytics'
import { isUrlAllowedByEcomTargets } from '@/utils/checkUrlTargets'
import { resolveEcomProviders } from '@/utils/resolveEcomProviders'
import { getSharedContactId } from '@/utils/contactIdentity'
import { getDeviceId } from '@/utils/device'

export type WidgetCloseAnimation = 'smooth' | 'fade' | 'pop' | 'slide' | 'blur'

function forceEmbeddedChatEnabled() {
  const cfg = getWidgetConfig()
  if (cfg && cfg.chatEnabled !== true) {
    setWidgetConfig({ ...cfg, chatEnabled: true })
  }
}

const CLOSE_ANIMATION_DURATIONS: Record<WidgetCloseAnimation, number> = {
  smooth: 300,
  fade: 200,
  pop: 200,
  slide: 250,
  blur: 250,
}

let _closeTimeoutId: ReturnType<typeof setTimeout> | null = null

type PopupPosition = { x: number; y: number }
type PopupSize = { width: number; height: number }
type BreakpointOverrides = {
  icon: WidgetBreakpointTab | null
  welcomeMessage: WidgetBreakpointTab | null
}

export type DemoActionType = 'SHOW_POPUP' | 'SHOW_CHAT' | 'SHOW_WELCOME_MESSAGE' | 'IDLE'
export type DemoAction = {
  type: DemoActionType
  data?: Record<string, unknown>
} | null

export type PreviewComponent = 'button' | 'formInput' | 'text' | null
export type PreviewOptions = {
  component: PreviewComponent
  variant?: 'primary' | 'secondary'
}

type WidgetStoreState = {
  initialized: boolean
  initializing: boolean

  demo: boolean
  isFloatingWidgetMode: boolean
  floatingWidgetOpen: boolean
  hamburgerMenuOpen: boolean
  popupPosition: PopupPosition
  overlayPosition: PopupPosition
  popupSize: PopupSize | null
  isDragging: boolean
  isOverlayDragging: boolean
  isResizing: boolean
  isClosing: boolean
  closeAnimationType: WidgetCloseAnimation
  breakpointOverrides: BreakpointOverrides
  urlAllowed: boolean
  demoAction: DemoAction
  previewOptions: PreviewOptions
  showGradientOverlay: boolean
}

type WidgetStoreActions = {
  initialize: (force?: boolean) => Promise<void>
  setFloatingWidgetOpen: (floatingWidgetOpen: boolean) => void
  setHamburgerMenuOpen: (hamburgerMenuOpen: boolean) => void
  setPopupPosition: (position: PopupPosition) => void
  setOverlayPosition: (position: PopupPosition) => void
  setPopupSize: (size: PopupSize) => void
  setIsDragging: (isDragging: boolean) => void
  setIsOverlayDragging: (isOverlayDragging: boolean) => void
  setIsResizing: (isResizing: boolean) => void
  closeFloatingWidget: () => void
  setCloseAnimationType: (type: WidgetCloseAnimation) => void
  resetPopupPosition: () => void
  resetOverlayPosition: () => void
  resetPopupSize: () => void
  setBreakpointOverrides: (overrides: Partial<BreakpointOverrides>) => void
  setDemoAction: (action: DemoAction) => void
  setShowGradientOverlay: (show: boolean) => void
}

export type WidgetStore = WidgetStoreState & WidgetStoreActions

const FLOATING_OPEN_KEY = 'widget-ui:floatingWidgetOpen'

function getInitialFloatingWidgetOpen(): boolean {
  if (isEmbedded()) return false
  if (window.location.search.includes('demo=true')) return false
  return sessionStorage.getItem(FLOATING_OPEN_KEY) === 'true'
}

const initialState: WidgetStoreState = {
  initialized: false,
  initializing: false,
  demo: isEmbedded() ? (getWidgetConfig()?.demo ?? false) : window.location.search.includes('demo=true'),
  isFloatingWidgetMode: isEmbedded()
    ? (getWidgetConfig()?.widgetMode ?? true)
    : window.location.search.includes('widgetMode=true'),
  floatingWidgetOpen: getInitialFloatingWidgetOpen(),
  hamburgerMenuOpen: false,
  popupPosition: { x: 0, y: 0 },
  overlayPosition: { x: 0, y: 0 },
  popupSize: null,
  isDragging: false,
  isOverlayDragging: false,
  isResizing: false,
  isClosing: false,
  closeAnimationType: 'smooth' as WidgetCloseAnimation,
  breakpointOverrides: {
    icon: null,
    welcomeMessage: null,
  },
  urlAllowed: true,
  demoAction: null,
  previewOptions: { component: null },
  showGradientOverlay: false,
}

export const useWidgetStore = create<WidgetStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setFloatingWidgetOpen: (floatingWidgetOpen: boolean) => {
          if (floatingWidgetOpen && get().isClosing) {
            if (_closeTimeoutId) {
              clearTimeout(_closeTimeoutId)
              _closeTimeoutId = null
            }
            set({ isClosing: false }, false, 'setFloatingWidgetOpen/cancelClose')
          }
          if (!get().demo) {
            sessionStorage.setItem(FLOATING_OPEN_KEY, String(floatingWidgetOpen))
          }
          set({ floatingWidgetOpen }, false, 'setFloatingWidgetOpen')
          if (isEmbedded()) {
            widgetBridge.emit(floatingWidgetOpen ? 'opened' : 'closed')
          }
          if (floatingWidgetOpen) {
            track('widget', 'widget_ui_shown', { isFloatingWidgetMode: get().isFloatingWidgetMode })
          }
        },
        setHamburgerMenuOpen: (hamburgerMenuOpen: boolean) =>
          set({ hamburgerMenuOpen }, false, 'setHamburgerMenuOpen'),
        setPopupPosition: (popupPosition) => set({ popupPosition }, false, 'setPopupPosition'),
        setOverlayPosition: (overlayPosition) => set({ overlayPosition }, false, 'setOverlayPosition'),
        setPopupSize: (popupSize) => set({ popupSize }, false, 'setPopupSize'),
        setIsDragging: (isDragging) => set({ isDragging }, false, 'setIsDragging'),
        setIsOverlayDragging: (isOverlayDragging) => set({ isOverlayDragging }, false, 'setIsOverlayDragging'),
        setIsResizing: (isResizing) => set({ isResizing }, false, 'setIsResizing'),
        closeFloatingWidget: () => {
          if (get().isClosing || !get().floatingWidgetOpen) return
          if (!get().isFloatingWidgetMode) {
            get().setFloatingWidgetOpen(false)
            return
          }
          set({ isClosing: true }, false, 'closeFloatingWidget')
          const type = get().closeAnimationType
          const duration = CLOSE_ANIMATION_DURATIONS[type]
          _closeTimeoutId = setTimeout(() => {
            _closeTimeoutId = null
            if (!get().isClosing) return
            set({ isClosing: false }, false, 'closeFloatingWidget/complete')
            get().setFloatingWidgetOpen(false)
          }, duration + 50)
        },
        setCloseAnimationType: (closeAnimationType) =>
          set({ closeAnimationType }, false, 'setCloseAnimationType'),
        resetPopupPosition: () => set({ popupPosition: { x: 0, y: 0 } }, false, 'resetPopupPosition'),
        resetOverlayPosition: () => set({ overlayPosition: { x: 0, y: 0 } }, false, 'resetOverlayPosition'),
        resetPopupSize: () => set({ popupSize: null }, false, 'resetPopupSize'),
        setBreakpointOverrides: (overrides) =>
          set(
            (state) => ({
              breakpointOverrides: { ...state.breakpointOverrides, ...overrides },
            }),
            false,
            'setBreakpointOverrides'
          ),
        setDemoAction: (demoAction) => set({ demoAction }, false, 'setDemoAction'),
        setShowGradientOverlay: (show) => set({ showGradientOverlay: show }, false, 'setShowGradientOverlay'),

        initialize: async (force?: boolean) => {
          if ((get().initialized || get().initializing) && !force) return
          set({ initializing: true }, false, 'initialize/start')

          const embeddedConfig = getWidgetConfig()

          initApiBase()

          const env = process.env.MODE
          const aiqEnv = process.env.AIQ_ENV

          const queryParams = new URLSearchParams(window.location.search)
          const isFloatingWidgetMode = embeddedConfig?.widgetMode ?? queryParams.get('widgetMode') === 'true'
          const demo = embeddedConfig?.demo ?? queryParams.get('demo') === 'true'

          // Optional identity hints from URL. Used by the chat flow (and any
          // other flow that reads contactID/globalID from useSessionStore).
          // Merchant embeds normally identify via URL detection or the sign-in
          // flow; these params are a first-class shortcut for identified
          // sessions (merchant deep links, dev/QA, dashboard previews).
          const urlContactID = queryParams.get('contactID')?.trim() || undefined
          const urlStoreID = queryParams.get('storeID')?.trim() || undefined

          const previewParam = queryParams.get('preview')
          const variantParam = queryParams.get('variant')
          const validComponents: PreviewComponent[] = ['button', 'formInput', 'text']
          const previewComponent = validComponents.includes(previewParam as PreviewComponent)
            ? (previewParam as PreviewComponent)
            : null
          const previewVariant =
            variantParam === 'primary' || variantParam === 'secondary' ? variantParam : undefined

          const previewOptions: PreviewOptions = {
            component: previewComponent,
            variant: previewVariant,
          }

          logger.log('initialize', { isFloatingWidgetMode, mode: env, demo, aiqEnv, previewOptions })

          set({ isFloatingWidgetMode, demo, previewOptions }, false, 'initialize/setWidgetMode')

          // Restore prior session locate result before /widget/locations so
          // distance sorting can use cached coords on MPA navigations, and so
          // we do not re-prompt after deny/dismiss on the next page load.
          hydrateUserDeviceLocationFromSession()

          const uids = await useSessionStore.getState().setInitialUIDs()
          if (!uids) {
            set({ initializing: false }, false, 'initialize/noUIDError')
            if (!isEmbedded()) {
              showErrorToast('No UID provided')
            }
            return
          }

          // Chat-only fast path: when the debug ?mode=chat query param is set
          // (or an embedded caller passes mode=chat via config), skip locations,
          // sign-in, ecom provider resolution, and geolocation. Settings are
          // still fetched so tenant-configured chat copy can apply.
          const modeIsChat = queryParams.get('mode') === 'chat'
          if (modeIsChat) {
            // In the normal path, useSessionStore.initialize() promotes the
            // first initialUID into currentUID. The chat-only fast path skips
            // that entirely, so promote it here — getCurrentUID() must resolve
            // for ensureSession/sendMessage to succeed.
            const chatUID = uids[0]
            useSessionStore.setState(
              {
                currentUID: chatUID,
                // URL-supplied contactID hydrates the shopper identity so chat
                // requests can include it without waiting for a sign-in.
                ...(urlContactID ? { contactID: urlContactID } : {}),
                // In chat-only there's no globalID selector; treat the URL
                // storeID as the widget's globalID too.
                ...(urlStoreID ? { currentGlobalID: urlStoreID } : {}),
              },
              false,
              'initialize/queryModeChat/session'
            )
            await useSessionStore.getState().fetchWidgetSettings(chatUID)
            forceEmbeddedChatEnabled()
            useSessionStore.setState(
              (state) => ({
                widgetSettings: {
                  ...(state.widgetSettings ?? {}),
                  enabled: true,
                  chatSettings: {
                    ...(state.widgetSettings?.chatSettings ?? {}),
                    enabled: true,
                  },
                  recommendationSettings: {
                    ...(state.widgetSettings?.recommendationSettings ?? {}),
                    enabled: false,
                  },
                },
              }),
              false,
              'initialize/queryModeChat/widgetSettings'
            )
            // Stub the locations store so App.tsx's embedded killswitch
            // (widgetEnabled !== true) doesn't null-out the render. When a URL
            // storeID is provided, synthesize a matching ecomLocation so
            // getStoreIDByGlobalID() resolves it downstream (chat POST body).
            const stubbedLocations: EcomLocation[] = urlStoreID
              ? [
                  {
                    uid: uids[0] ?? '',
                    globalID: urlStoreID,
                    storeID: urlStoreID,
                    locationName: 'Debug Store',
                    subEcomLocations: [],
                    distanceInMiles: 0,
                  },
                ]
              : []
            useLocationsStore.setState(
              {
                data: {
                  ecomLocations: stubbedLocations,
                  loyaltyLocations: [],
                  enabled: true,
                  code: 200,
                  success: true,
                },
                loaded: true,
              },
              false,
              'initialize/queryModeChat/locationsStub'
            )
            set({ urlAllowed: true, initialized: true, initializing: false }, false, 'initialize/chatOnly')
            // Fast path only initializes chat — do NOT emit widget_initialized
            // (that event is reserved for the terpli/recommendation widget).
            track('chat', 'chat_initialized', {
              demo,
              source: 'queryModeChat',
              hasStoreID: Boolean(urlStoreID),
              hasContactID: Boolean(urlContactID),
            })
            const [{ useFlowState }, { WidgetFlow }] = await Promise.all([
              import('./useFlowState'),
              import('@/types'),
            ])
            useFlowState.getState().setCurrentFlow(WidgetFlow.Chat)
            return
          }

          const data = await useLocationsStore.getState().initialize()
          if (!data) {
            set({ initializing: false }, false, 'initialize/noDataError')
            return
          }

          if (isEmbedded() && !demo && data.enabled !== true) {
            // Terpli/store widget not enabled for this tenant. Fall back to
            // chat-only: the pixel only spawns widget-ui when either terpli
            // or chat is on (see /aiq-sdk/widget/enabled), so if terpli is
            // off here chat must be on. /widget/settings can omit
            // chatSettings entirely for chat-only tenants, so we cannot gate
            // on chatSettings.enabled=true (that was the previous behavior
            // and it silently null-rendered chat-only tenants).
            const chatUID = uids[0]
            useSessionStore.setState(
              {
                currentUID: chatUID,
                ...(urlContactID ? { contactID: urlContactID } : {}),
                ...(urlStoreID ? { currentGlobalID: urlStoreID } : {}),
              },
              false,
              'initialize/chatOnlyFallback/session'
            )
            await useSessionStore.getState().fetchWidgetSettings(chatUID)
            // Force chat-only UX: chat on, recommendations off. Mirrors the
            // ?mode=chat fast path so WelcomeScreen auto-navigates into chat
            // instead of stacking a Terpli CTA the tenant hasn't configured.
            // Honor spawn chatEnabled=false (no addendum) so this fallback
            // cannot re-enable chat after pixel already gated it off.
            if (getWidgetConfig()?.chatEnabled !== false) {
              forceEmbeddedChatEnabled()
            }
            useSessionStore.setState(
              (state) => ({
                widgetSettings: {
                  ...(state.widgetSettings ?? {}),
                  enabled: true,
                  chatSettings: {
                    ...(state.widgetSettings?.chatSettings ?? {}),
                    enabled: true,
                  },
                  recommendationSettings: {
                    ...(state.widgetSettings?.recommendationSettings ?? {}),
                    enabled: false,
                  },
                },
              }),
              false,
              'initialize/chatOnlyFallback/widgetSettings'
            )
            const stubbedLocations: EcomLocation[] = urlStoreID
              ? [
                  {
                    uid: chatUID ?? '',
                    globalID: urlStoreID,
                    storeID: urlStoreID,
                    locationName: 'Debug Store',
                    subEcomLocations: [],
                    distanceInMiles: 0,
                  },
                ]
              : []
            useLocationsStore.setState(
              {
                data: {
                  ecomLocations: stubbedLocations,
                  loyaltyLocations: data.loyaltyLocations ?? [],
                  enabled: true,
                  code: 200,
                  success: true,
                },
                loaded: true,
              },
              false,
              'initialize/chatOnlyFallback/locationsStub'
            )
            set(
              { urlAllowed: true, initialized: true, initializing: false },
              false,
              'initialize/chatOnlyFallback'
            )
            track('chat', 'chat_initialized', {
              demo,
              source: 'chatOnlyFallback',
              hasStoreID: Boolean(urlStoreID),
              hasContactID: Boolean(urlContactID),
            })
            const [{ useFlowState }, { WidgetFlow }] = await Promise.all([
              import('./useFlowState'),
              import('@/types'),
            ])
            useFlowState.getState().setCurrentFlow(WidgetFlow.Chat)
            return
          }

          const locationsData = data ?? getLocationsResponseData()

          const urlAllowed = isUrlAllowedByEcomTargets(window.location.href, locationsData.ecomLocations ?? [])
          set({ urlAllowed }, false, 'initialize/urlAllowed')

          await useSessionStore.getState().initialize(locationsData)

          // Hydrate identity hints from URL after session init so they aren't
          // clobbered by locations-driven UID/globalID resolution above.
          //   ?contactID=…  — hydrates useSessionStore.contactID
          //   ?storeID=…    — resolves to the matching EcomLocation.globalID
          //                   and activates that store (so downstream calls
          //                   like chat's getStoreIDByGlobalID resolve it).
          if (urlStoreID) {
            const matching = (locationsData.ecomLocations ?? []).find(
              (loc) => asTrimmedId(loc.storeID) === urlStoreID || loc.globalID === urlStoreID
            )
            if (matching && matching.globalID !== useSessionStore.getState().currentGlobalID) {
              useSessionStore.getState().setCurrentGlobalID(matching.globalID)
            } else if (!matching) {
              logger.log('initialize/urlStoreID unmatched', { urlStoreID })
            }
          }
          if (urlContactID) {
            useSessionStore.getState().setContactID(urlContactID)
          }

          // Sync with shared aiq_contact_id: mirror a Zustand-restored contact
          // (setContactID early-return persists it), or adopt from the pixel key.
          const sessionContactID = useSessionStore.getState().contactID
          if (sessionContactID) {
            useSessionStore.getState().setContactID(sessionContactID)
          } else {
            const sharedContactID = getSharedContactId()
            if (sharedContactID) {
              useSessionStore.getState().setContactID(sharedContactID)
              logger.log('initialize/sharedContact hydrate', { contactID: sharedContactID })
            }
          }

          // Debug ?mode=both: force both chat and recommendations on top of
          // whatever the tenant returned. Runs after /widget/settings so the
          // rec flow keeps its real config; only the enabled flags are coerced.
          // Unlike ?mode=chat this does NOT skip the network bootstrap.
          const modeIsBoth = queryParams.get('mode') === 'both'
          if (modeIsBoth) {
            forceEmbeddedChatEnabled()
            useSessionStore.setState(
              (state) => ({
                widgetSettings: {
                  ...(state.widgetSettings ?? {}),
                  enabled: true,
                  chatSettings: {
                    ...(state.widgetSettings?.chatSettings ?? {}),
                    enabled: true,
                  },
                  recommendationSettings: {
                    ...(state.widgetSettings?.recommendationSettings ?? {}),
                    enabled: true,
                  },
                },
              }),
              false,
              'initialize/queryModeBoth/widgetSettings'
            )
            // useFlowState persists currentFlow/currentStep to sessionStorage.
            // If the previous session was ?mode=chat we'd hydrate straight
            // back into the Chat flow instead of landing on Welcome — reset
            // so the dual-CTA welcome is what devs see.
            const { useFlowState } = await import('./useFlowState')
            useFlowState.setState(
              {
                currentFlow: undefined,
                currentStep: WidgetStep.Welcome,
                previousStep: undefined,
                skippedSteps: {},
              },
              false,
              'initialize/queryModeBoth/resetFlow'
            )
          }

          const { currentUID, currentGlobalID, currentMenuType } = useSessionStore.getState()
          const ecomProviders = resolveEcomProviders(
            locationsData,
            currentGlobalID,
            currentMenuType,
            currentUID
          )
          if (ecomProviders.length > 0) {
            widgetBridge.emit('ecom:providers', { providers: ecomProviders })
          }

          set({ initialized: true, initializing: false }, false, 'initialize/complete')

          // Best-effort device auto-identify — never block init on /devices/lookup.
          // Skipped in demo (analytics + shared contact persistence already skip demo).
          if (!demo && !useSessionStore.getState().contactID) {
            const uid = useSessionStore.getState().currentUID
            const deviceId = getDeviceId()
            if (uid && deviceId && deviceId !== 'unknown') {
              void lookupDeviceContacts(uid, deviceId).then(([lookup, lookupErr]) => {
                if (lookupErr) {
                  logger.log('initialize/deviceLookup failed', { lookupErr })
                  return
                }
                // If the merchant switched mid-flight (setCurrentGlobalID clears
                // contactID as part of the UID swap), the response belongs to
                // the previous merchant and must not be persisted under the
                // new session's aiq_contact_id.
                if (useSessionStore.getState().currentUID !== uid) {
                  logger.log('initialize/deviceLookup uid changed mid-flight', {
                    originalUID: uid,
                    currentUID: useSessionStore.getState().currentUID,
                  })
                  return
                }
                if (useSessionStore.getState().contactID) return
                if (lookup?.contactIDs?.length === 1) {
                  const contactID = lookup.primaryContactID || lookup.contactIDs[0]
                  useSessionStore.getState().setContactID(contactID)
                  logger.log('initialize/deviceLookup autoIdentify', { contactID })
                }
              })
            }
          }

          // Emit init events per feature that actually turned on for this
          // tenant. widget_initialized is reserved for terpli/recommendation
          // (the historical "widget") — chat gets its own chat_initialized.
          const settledSettings = useSessionStore.getState().widgetSettings
          const recIsEnabled = settledSettings?.recommendationSettings?.enabled !== false
          const chatIsEnabled = resolveChatEnabled(settledSettings)
          const deviceLocationEnabled = settledSettings?.recommendationSettings?.enableDeviceLocation !== false

          if (recIsEnabled) {
            track('widget', 'widget_initialized', { demo })
          }
          if (chatIsEnabled) {
            const chatSource = modeIsBoth ? 'queryModeBoth' : 'widgetSettings'
            track('chat', 'chat_initialized', { demo, source: chatSource })
          }

          if (deviceLocationEnabled && (!isEmbedded() || locationsData.enabled === true)) {
            // Non-blocking: first paint should not wait on the permission dialog.
            // On a freshly acquired position, re-fetch so store order reflects distance.
            // Skip the refresh when we already had session-cached coords (used above).
            const hadCachedCoords = Boolean(useUserDeviceLocationStore.getState().coords)
            void useUserDeviceLocationStore
              .getState()
              .locateUser()
              .then((coords) => {
                if (!coords || hadCachedCoords) return
                const sessionUIDs = useSessionStore.getState().initialUIDs
                if (!sessionUIDs.length) return
                void useLocationsStore
                  .getState()
                  .fetchLocations({ uids: sessionUIDs, coords, preview: isDemoMode() }, 'all', true)
              })
          }

          const onNavigationChange = () => {
            const locations = getLocationsResponseData()

            const next = isUrlAllowedByEcomTargets(window.location.href, locations.ecomLocations ?? [])
            if (next !== get().urlAllowed) {
              set({ urlAllowed: next }, false, 'urlAllowed/navigation')
            }

            const bestMatch = findBestMatchingGlobalID(locations.ecomLocations ?? [])
            if (bestMatch) {
              const session = useSessionStore.getState()
              const globalIDChanged = bestMatch.globalID !== session.currentGlobalID
              const menuTypeChanged = bestMatch.menuType !== session.currentMenuType

              if (globalIDChanged) {
                session.setCurrentGlobalID(bestMatch.globalID, bestMatch.menuType)
              } else if (menuTypeChanged) {
                session.setCurrentMenuType(bestMatch.menuType)
              }
            }
          }

          window.addEventListener('popstate', onNavigationChange)
          window.addEventListener('aiq:history-change', onNavigationChange)
        },
      }),
      createPersistOptions<WidgetStore, 'popupPosition' | 'popupSize' | 'overlayPosition'>({
        name: 'widget',
        pick: ['popupPosition', 'popupSize', 'overlayPosition'],
      })
    ),
    { name: 'WidgetStore', enabled: process.env.NODE_ENV === 'development' }
  )
)

export const isDemoMode = () => {
  return isEmbedded()
    ? (getWidgetConfig()?.demo ?? false)
    : new URLSearchParams(window.location.search).get('demo') === 'true'
}

const REVIEWS_V2_STORAGE_KEY = 'aiq:featureFlags:reviewsV2'

/**
 * Optional Reviews V2 override. Live visibility is dash Enable Reviews
 * (`reviewSettings.enabled`). This flag still works on merchant embeds via
 * `?reviewsV2=true`, host localStorage, or embed `{ reviewsV2: true }`.
 */
export const isReviewsV2Enabled = (): boolean => {
  if (getWidgetConfig()?.reviewsV2 === true) {
    return true
  }
  try {
    if (new URLSearchParams(window.location.search).get('reviewsV2') === 'true') {
      return true
    }
    return window.localStorage.getItem(REVIEWS_V2_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}
